

import React, { createContext, useState, useContext, useRef, useEffect, forwardRef } from "react"
import { createPortal } from "react-dom"

// Create context for the tooltip
const TooltipContext = createContext(null)

// Provider component to manage tooltip state
export function TooltipProvider({ children, delayDuration = 300 }) {
  const [open, setOpen] = useState(false)
  const [content, setContent] = useState(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [triggerRect, setTriggerRect] = useState(null)
  const [side, setSide] = useState("top")
  const [align, setAlign] = useState("center")

  const value = {
    open,
    setOpen,
    content,
    setContent,
    position,
    setPosition,
    triggerRect,
    setTriggerRect,
    delayDuration,
    side,
    setSide,
    align,
    setAlign,
  }

  return <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
}

// Hook to access tooltip context
function useTooltip() {
  const context = useContext(TooltipContext)
  if (!context) {
    throw new Error("useTooltip must be used within a TooltipProvider")
  }
  return context
}

// Trigger component that shows tooltip on hover
export const TooltipTrigger = forwardRef(function TooltipTrigger({ children, asChild, ...props }, forwardedRef) {
  const { setOpen, setContent, setTriggerRect, delayDuration, setSide, setAlign } = useTooltip()

  const triggerRef = useRef(null)
  const timeoutRef = useRef(null)

  // Combine the forwarded ref with our local ref
  const handleRef = (element) => {
    triggerRef.current = element
    if (typeof forwardedRef === "function") {
      forwardedRef(element)
    } else if (forwardedRef) {
      forwardedRef.current = element
    }
  }

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect())
        setOpen(true)
      }
    }, delayDuration)
  }

  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setOpen(false)
    }, 100)
  }

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  // If asChild is true, clone the child element and add our props
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ref: handleRef,
      onMouseEnter: (e) => {
        handleMouseEnter()
        children.props.onMouseEnter?.(e)
      },
      onMouseLeave: (e) => {
        handleMouseLeave()
        children.props.onMouseLeave?.(e)
      },
      "aria-describedby": "tooltip",
      ...props,
    })
  }

  // Otherwise, wrap the children in a span
  return (
    <span
      ref={handleRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-describedby="tooltip"
      {...props}
    >
      {children}
    </span>
  )
})

// Content component that displays the tooltip
export function TooltipContent({ children, side = "top", align = "center", className = "", hidden = false, ...props }) {
  const { open, setOpen, triggerRect, setSide, setAlign } = useTooltip()
  const contentRef = useRef(null)

  useEffect(() => {
    setSide(side)
    setAlign(align)
  }, [side, align, setSide, setAlign])

  useEffect(() => {
    const handleMouseEnter = () => {
      setOpen(true)
    }

    const handleMouseLeave = () => {
      setOpen(false)
    }

    const contentElement = contentRef.current
    if (contentElement) {
      contentElement.addEventListener("mouseenter", handleMouseEnter)
      contentElement.addEventListener("mouseleave", handleMouseLeave)
    }

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("mouseenter", handleMouseEnter)
        contentElement.removeEventListener("mouseleave", handleMouseLeave)
      }
    }
  }, [setOpen])

  if (!open || hidden || !triggerRect) return null

  // Calculate position based on side and align
  const getPosition = () => {
    if (!triggerRect || !contentRef.current) return { top: 0, left: 0 }

    const contentRect = contentRef.current.getBoundingClientRect()
    const scrollX = window.scrollX
    const scrollY = window.scrollY

    let top = 0
    let left = 0

    switch (side) {
      case "top":
        top = triggerRect.top - contentRect.height - 8 + scrollY
        break
      case "bottom":
        top = triggerRect.bottom + 8 + scrollY
        break
      case "left":
        left = triggerRect.left - contentRect.width - 8 + scrollX
        break
      case "right":
        left = triggerRect.right + 8 + scrollX
        break
      default:
        top = triggerRect.top - contentRect.height - 8 + scrollY
    }

    switch (align) {
      case "start":
        if (side === "top" || side === "bottom") {
          left = triggerRect.left + scrollX
        } else {
          top = triggerRect.top + scrollY
        }
        break
      case "center":
        if (side === "top" || side === "bottom") {
          left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2 + scrollX
        } else {
          top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2 + scrollY
        }
        break
      case "end":
        if (side === "top" || side === "bottom") {
          left = triggerRect.right - contentRect.width + scrollX
        } else {
          top = triggerRect.bottom - contentRect.height + scrollY
        }
        break
      default:
        if (side === "top" || side === "bottom") {
          left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2 + scrollX
        } else {
          top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2 + scrollY
        }
    }

    // Ensure tooltip stays within viewport
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    if (left < 10) left = 10
    if (left + contentRect.width > viewportWidth - 10) {
      left = viewportWidth - contentRect.width - 10
    }

    if (top < 10) top = 10
    if (top + contentRect.height > viewportHeight - 10) {
      top = viewportHeight - contentRect.height - 10
    }

    return { top, left }
  }

  const position = getPosition()

  // Use createPortal to render the tooltip at the root level of the DOM
  return createPortal(
    <div
      ref={contentRef}
      role="tooltip"
      id="tooltip"
      className={`tooltip-content ${className}`}
      style={{
        position: "absolute",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.8)",
        color: "white",
        padding: "6px 10px",
        borderRadius: "4px",
        fontSize: "14px",
        maxWidth: "300px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.2)",
        pointerEvents: "auto",
        ...props.style,
      }}
      {...props}
    >
      {children}
      <div
        className="tooltip-arrow"
        style={{
          position: "absolute",
          width: "8px",
          height: "8px",
          background: "rgba(0, 0, 0, 0.8)",
          transform: "rotate(45deg)",
          ...(side === "top" && { bottom: "-4px", left: "50%", marginLeft: "-4px" }),
          ...(side === "bottom" && { top: "-4px", left: "50%", marginLeft: "-4px" }),
          ...(side === "left" && { right: "-4px", top: "50%", marginTop: "-4px" }),
          ...(side === "right" && { left: "-4px", top: "50%", marginTop: "-4px" }),
        }}
      />
    </div>,
    document.body,
  )
}

// Main Tooltip component for convenience
export function Tooltip({ children }) {
  return children
}

// Export all components
export { TooltipContext }

